import React, { useEffect, useState, useRef, useId } from 'react';
import { playSoundEffect } from '../hooks/useSoundEffects';
import { getAttackSound } from '../sounds/attackSoundMapping';

/**
 * DeployTargetAnimation - Projectile animation from source to target using DOM lookup for accurate positioning
 * Uses DOM lookup for unit slots (same as StrategemTargetAnimation) to ensure accurate positioning
 * regardless of screen size, zoom, or layout changes. Falls back to hardcoded coordinates if DOM lookup fails.
 * @param {Object} props
 * @param {Object} props.fromSlot - { zone, index, playerId } - Source slot
 * @param {Object} props.toSlot - { zone, index, playerId } - Target slot
 * @param {string} props.animationType - 'friendly', 'opponent', or 'both'
 * @param {number} [props.deployUnitId] - Order ID of deploying unit (e.g. 239 for Kek → kek_atom.png; 70 Nomadic Bladecaster → dagger.png; 71 First Wave Ascendants / 73 Second Wave Redeemers → bullet.png, top toward target like Guided Missile)
 * @param {string} [props.deployUnitName] - Deploying unit name (for attack sound inference)
 * @param {string} [props.deployUnitAttackSoundType] - orders_units.attack_sound_type (same as regular attacks)
 * @param {boolean} [props.isRedeemDamageAnimation] - Redeem damage uses source unit attack sound, not deploy SFX
 * @param {string} [props.deployAnimationVariant] - e.g. 'encrazed_bombardier_fire_arc' for half-circle fire wave; 'frontal_harassment_blue_cross' for unit 100 barrier damage; 'skyfire_strike_blue_stick' for Skyfire Strike (275) drone charge
 * @param {Function} props.onComplete - Callback when animation completes
 * @param {Function} [props.onDamageAtTarget] - Called when projectile reaches target (before onComplete)
 * @param {Function} [props.onFireArcHealthCountdownComplete] - Encrazed Bombardier fire arc only: HP counter FX, no extra damage pop
 * @param {number} props.animationSpeedMultiplier - Speed multiplier (1 = normal, 0.5 = 2x slower)
 */
// Deploy units that use dagger.png / bullet.png with top aimed at target (same rotation convention as Strategem Guided Missile: atan2 + 90°)
const DEPLOY_DAGGER_ORDER_IDS = new Set([70]); // 70 Nomadic Bladecaster
const DEPLOY_BULLET_ORDER_IDS = new Set([71, 73]); // 71 First Wave Ascendants, 73 Second Wave Redeemers

export const DeployTargetAnimation = ({
  fromSlot,
  toSlot,
  animationType,
  deployUnitId,
  deployUnitName,
  deployUnitAttackSoundType,
  isRedeemDamageAnimation = false,
  deployAnimationVariant,
  onComplete,
  onDamageAtTarget,
  onFireArcHealthCountdownComplete,
  animationSpeedMultiplier = 1,
}) => {
  const fireArcGradientId = useId().replace(/:/g, '');
  const crossGlowFilterId = useId().replace(/:/g, '');
  const toxSoundPlayedRef = useRef(false);
  const bulletSoundPlayedRef = useRef(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [opacity, setOpacity] = useState(1);
  const [daggerRotationDeg, setDaggerRotationDeg] = useState(0);
  const [fireArc, setFireArc] = useState(null);
  const [smokeTrail, setSmokeTrail] = useState(null);
  const animationRef = useRef(null);
  const timeoutRef = useRef(null);
  const startTimeRef = useRef(null);
  const isAnimatingRef = useRef(false);
  const fireArcRafRef = useRef(null);
  const fireArcPlaybackStartedRef = useRef(false);
  const onFireArcHealthCountdownCompleteRef = useRef(onFireArcHealthCountdownComplete);
  const onCompleteRef = useRef(onComplete);
  const onDamageAtTargetRef = useRef(onDamageAtTarget);

  useEffect(() => {
    onFireArcHealthCountdownCompleteRef.current = onFireArcHealthCountdownComplete;
    onCompleteRef.current = onComplete;
    onDamageAtTargetRef.current = onDamageAtTarget;
  });

  const isDaggerDeployUnit =
    deployUnitId != null && DEPLOY_DAGGER_ORDER_IDS.has(Number(deployUnitId));
  const isBulletDeployUnit =
    deployUnitId != null && DEPLOY_BULLET_ORDER_IDS.has(Number(deployUnitId));
  const isRedeemBulletAnimation = isRedeemDamageAnimation === true;
  const isFrontalHarassmentBlueCross =
    deployAnimationVariant === 'frontal_harassment_blue_cross';
  const isSkyfireStrikeBlueStick = deployAnimationVariant === 'skyfire_strike_blue_stick';
  const isKinshipStrikeBlackStick = deployAnimationVariant === 'kinship_strike_black_stick';
  const isRotatingDeployProjectile =
    isDaggerDeployUnit ||
    isBulletDeployUnit ||
    isRedeemBulletAnimation ||
    isFrontalHarassmentBlueCross ||
    isSkyfireStrikeBlueStick ||
    isKinshipStrikeBlackStick;
  const isToxengineerDeploy = Number(deployUnitId) === 166;
  const KEK_ATOM_DEPLOY_IDS = new Set([239, 69, 119, 249, 391]);
  const useKekAtomImage =
    deployUnitId != null && KEK_ATOM_DEPLOY_IDS.has(Number(deployUnitId));

  // Hardcoded slot coordinates (cell-based) - provided by user
  // Grid format: "cell 10,4" means cellX=10 (horizontal), cellY=4 (vertical)
  // Grid uses 20px cell size (from GridOverlay.js)
  // Format: { zone_index: { cellX, cellY } }
  const slotCoordinates = {
    // Opponent Front (slots 1-5)
    'opponent_front_0': { cellX: 4, cellY: 10 },   // Slot 1
    'opponent_front_1': { cellX: 14, cellY: 10 }, // Slot 2
    'opponent_front_2': { cellX: 24, cellY: 9 },  // Slot 3
    'opponent_front_3': { cellX: 32, cellY: 10 }, // Slot 4
    'opponent_front_4': { cellX: 42, cellY: 9 },  // Slot 5
    
    // Opponent Flank (slots 6-10)
    'opponent_flank_0': { cellX: 52, cellY: 10 }, // Slot 6
    'opponent_flank_1': { cellX: 52, cellY: 11 }, // Slot 7
    'opponent_flank_2': { cellX: 70, cellY: 10 }, // Slot 8
    'opponent_flank_3': { cellX: 79, cellY: 10 }, // Slot 9
    'opponent_flank_4': { cellX: 86, cellY: 10 }, // Slot 10
    
    // Your Front (slots 11-15)
    'your_front_0': { cellX: 3, cellY: 18 },   // Slot 11
    'your_front_1': { cellX: 15, cellY: 18 }, // Slot 12
    'your_front_2': { cellX: 22, cellY: 18 }, // Slot 13
    'your_front_3': { cellX: 32, cellY: 17 }, // Slot 14
    'your_front_4': { cellX: 39, cellY: 18 }, // Slot 15
    
    // Your Flank (slots 16-20)
    'your_flank_0': { cellX: 52, cellY: 18 }, // Slot 16
    'your_flank_1': { cellX: 62, cellY: 18 }, // Slot 17
    'your_flank_2': { cellX: 69, cellY: 17 }, // Slot 18
    'your_flank_3': { cellX: 81, cellY: 18 }, // Slot 19
    'your_flank_4': { cellX: 89, cellY: 17 }, // Slot 20

    // Drone zone coordinates (same as StrategemTargetAnimation - for Kek → drone and similar deploy targeting)
    'opponent_drone_0': { cellX: 10, cellY: 6 },
    'opponent_drone_1': { cellX: 25, cellY: 6 },
    'opponent_drone_2': { cellX: 40, cellY: 6 },
    'opponent_drone_3': { cellX: 55, cellY: 6 },
    'your_drone_0': { cellX: 10, cellY: 22 },
    'your_drone_1': { cellX: 25, cellY: 22 },
    'your_drone_2': { cellX: 40, cellY: 22 },
    'your_drone_3': { cellX: 55, cellY: 22 },
  };

  // Convert cell coordinates to pixel coordinates
  // Grid uses 20px cells (gridSize = 20 from GridOverlay)
  // Grid shows "cell X,Y" where X is horizontal, Y is vertical
  const GRID_CELL_SIZE = 20; // Match GridOverlay gridSize
  const GRID_OFFSET_X = 0;
  const GRID_OFFSET_Y = 0;
  /** Above .state-updating-overlay (10000) and emote UI (10002); below deploy-drag overlay (100002). */
  const DEPLOY_VFX_Z_INDEX = 50000;

  const cellToPixel = (cellX, cellY) => {
    // Grid format: "cell 10,4" = X=10 (horizontal), Y=4 (vertical)
    // Convert to pixel: cellX * gridSize + center of cell
    return {
      x: GRID_OFFSET_X + (cellX * GRID_CELL_SIZE) + (GRID_CELL_SIZE / 2),
      y: GRID_OFFSET_Y + (cellY * GRID_CELL_SIZE) + (GRID_CELL_SIZE / 2)
    };
  };

  const getSlotPixelPosition = (zone, index, playerId = null, instanceId = null) => {
    // Handle general targets - use DOM lookup
    if (zone === 'general') {
      // Find general element by data-zone attribute
      // playerId here is 'your' or 'opponent' (already converted by BattleUI)
      let generalElement = null;
      
      console.log(`🎬 DeployTargetAnimation: Looking for general element, playerId: ${playerId}`);
      
      if (playerId === 'your') {
        generalElement = document.querySelector(`[data-zone="player_general"]`);
        console.log(`🎬 DeployTargetAnimation: Looking for player_general, found: ${!!generalElement}`);
      } else if (playerId === 'opponent') {
        generalElement = document.querySelector(`[data-zone="opponent_general"]`);
        console.log(`🎬 DeployTargetAnimation: Looking for opponent_general, found: ${!!generalElement}`);
      } else {
        // Fallback: try both if playerId is not 'your' or 'opponent'
        console.warn(`⚠️ DeployTargetAnimation: Unknown playerId "${playerId}", trying both generals`);
        generalElement = document.querySelector(`[data-zone="player_general"]`) || 
                         document.querySelector(`[data-zone="opponent_general"]`);
      }
      
      if (generalElement) {
        const rect = generalElement.getBoundingClientRect();
        console.log(`🎬 DeployTargetAnimation: Found general element at (${rect.left + rect.width / 2}, ${rect.top + rect.height / 2})`);
        return {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2
        };
      }
      
      // Fallback: approximate general positions based on playerId
      console.warn(`⚠️ DeployTargetAnimation: Could not find general element for ${playerId}, using fallback`);
      // Use different fallback positions for player vs opponent general
      if (playerId === 'your') {
        return { x: 200, y: 600 }; // Approximate player general position
      } else {
        return { x: 1600, y: 100 }; // Approximate opponent general position
      }
    }

    // Handle drone targeting (e.g. Kek → drone) - use DOM lookup so projectile goes to actual drone
    if (zone === 'opponent_drone' || zone === 'your_drone') {
      const droneElement =
        (instanceId &&
          document.querySelector(
            `[data-drone-zone="${zone}"][data-instance-id="${String(instanceId)}"]`
          )) ||
        document.querySelector(`[data-drone-zone="${zone}"][data-drone-index="${index}"]`);
      if (droneElement) {
        const rect = droneElement.getBoundingClientRect();
        return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
      }
      const droneKey = `${zone}_${index}`;
      const coords = slotCoordinates[droneKey];
      if (coords) {
        return cellToPixel(coords.cellX, coords.cellY);
      }
      console.warn(`⚠️ DeployTargetAnimation: Drone element not found for ${zone}[${index}], using fallback`);
      return { x: 200, y: 200 };
    }

    // ✅ FIX: Use DOM lookup for unit slots (same as StrategemTargetAnimation)
    // This ensures accurate positioning regardless of screen size, zoom, or layout changes
    // Build the correct zone selector
    let zoneForSelector = zone;
    
    // If zone doesn't already include player prefix, add it based on playerId
    if (zone === 'front' || zone === 'flank') {
      const playerPrefix = (playerId === 'opponent') ? 'opponent' : 'your';
      zoneForSelector = `${playerPrefix}_${zone}`;
    }
    
    // Try DOM lookup first — prefer instanceId so duplicate unit types hit the correct card
    let slotElement = null;
    if (instanceId) {
      const suffix = `_${String(instanceId)}`;
      const unitKeyNodes = document.querySelectorAll('[data-unit-key]');
      for (let i = 0; i < unitKeyNodes.length; i += 1) {
        const key = unitKeyNodes[i].getAttribute('data-unit-key') || '';
        if (key.endsWith(suffix)) {
          slotElement = unitKeyNodes[i].closest('[data-slot-zone]') || unitKeyNodes[i];
          break;
        }
      }
      if (!slotElement) {
        const byInstanceId = document.querySelector(`[data-instance-id="${String(instanceId)}"]`);
        if (byInstanceId) {
          slotElement = byInstanceId.closest('[data-slot-zone]') || byInstanceId;
        }
      }
    }
    if (!slotElement) {
      slotElement = document.querySelector(`[data-slot-zone="${zoneForSelector}"][data-slot-index="${index}"]`) ||
                     document.querySelector(`[data-slot-zone="${zone}"][data-slot-index="${index}"]`);
    }
    
    if (slotElement) {
      const rect = slotElement.getBoundingClientRect();
      console.log(`🎬 DeployTargetAnimation: Found slot element via DOM lookup at (${rect.left + rect.width / 2}, ${rect.top + rect.height / 2})`);
      return {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2
      };
    }
    
    // Fallback to hardcoded coordinates if DOM lookup fails
    // Use zoneForSelector (normalized zone with your_/opponent_ prefix) so slotKey matches slotCoordinates
    // e.g. "your_front_0" not "front_0" - fixes animation from wrong start (0,0) when Kek in some slots targets drone
    console.warn(`⚠️ DeployTargetAnimation: DOM lookup failed for ${zoneForSelector}[${index}], using hardcoded coords`);
    const slotKey = `${zoneForSelector}_${index}`;
    const coords = slotCoordinates[slotKey];
    
    if (!coords) {
      console.warn(`⚠️ DeployTargetAnimation: No coordinates for ${slotKey}`);
      return { x: 0, y: 0 };
    }
    
    return cellToPixel(coords.cellX, coords.cellY);
  };

  useEffect(() => {
    toxSoundPlayedRef.current = false;
    bulletSoundPlayedRef.current = false;
  }, [fromSlot?.zone, fromSlot?.index, toSlot?.zone, toSlot?.index]);

  useEffect(() => {
    if (deployAnimationVariant !== 'toxengineer_black_smoke' && !isToxengineerDeploy) {
      return;
    }
    if (toxSoundPlayedRef.current) {
      return;
    }
    toxSoundPlayedRef.current = true;
    try {
      playSoundEffect('/sounds/toxengineer.mp3', 0.6);
    } catch (error) {
      console.error('❌ DeployTargetAnimation: failed to play toxengineer.mp3', error);
    }
  }, [deployAnimationVariant, isToxengineerDeploy]);

  const shouldPlaySourceAttackSound = isBulletDeployUnit || isRedeemDamageAnimation === true;

  const playSourceAttackSound = () => {
    if (!shouldPlaySourceAttackSound || bulletSoundPlayedRef.current) {
      return;
    }
    if (isRedeemDamageAnimation && !deployUnitAttackSoundType) {
      throw new Error(
        `DeployTargetAnimation: redeem damage animation missing deployUnitAttackSoundType for unit ${deployUnitId}`
      );
    }
    bulletSoundPlayedRef.current = true;
    try {
      const attackSoundPath = getAttackSound({
        id: deployUnitId,
        name: deployUnitName,
        attack_sound_type: deployUnitAttackSoundType,
      });
      playSoundEffect(attackSoundPath, 0.6);
    } catch (error) {
      console.error('❌ DeployTargetAnimation: failed to play source attack sound', error);
    }
  };

  useEffect(() => {
    if (!fromSlot || !toSlot) {
      console.log('⚠️ DeployTargetAnimation: Skipping - missing props', { fromSlot, toSlot });
      return;
    }

    if (deployAnimationVariant === 'encrazed_bombardier_fire_arc') {
      if (fireArcPlaybackStartedRef.current || isAnimatingRef.current) {
        return undefined;
      }
      fireArcPlaybackStartedRef.current = true;

      let fireArcEffectAborted = false;

      const speedFactor = animationSpeedMultiplier > 0 ? animationSpeedMultiplier : 1;
      const BASE_DURATION_MS = 520;

      /**
       * Same-zone deploy hits the mirror row (your_front → opponent_front; opponent_front → your_front when spectating).
       * Aim and width must use viewport DOM — mixing getBoundingClientRect with cellToPixel breaks atan2.
       */
      const getTargetRowZoneKey = (deployerZoneFull) => {
        if (deployerZoneFull === 'your_front') return 'opponent_front';
        if (deployerZoneFull === 'your_flank') return 'opponent_flank';
        if (deployerZoneFull === 'opponent_front') return 'your_front';
        if (deployerZoneFull === 'opponent_flank') return 'your_flank';
        throw new Error(
          `DeployTargetAnimation encrazed_bombardier_fire_arc: unsupported fromSlot.zone "${deployerZoneFull}"`
        );
      };

      const getOpponentRowAimFromDom = (opponentZoneKey) => {
        const slots = document.querySelectorAll(`[data-slot-zone="${opponentZoneKey}"][data-slot-index]`);
        const xs = [];
        const ys = [];
        slots.forEach((el) => {
          const rect = el.getBoundingClientRect();
          xs.push(rect.left + rect.width / 2);
          ys.push(rect.top + rect.height / 2);
        });
        if (xs.length === 0) {
          for (let i = 0; i < 5; i++) {
            const el = document.querySelector(
              `[data-slot-zone="${opponentZoneKey}"][data-slot-index="${i}"]`
            );
            if (el) {
              const rect = el.getBoundingClientRect();
              xs.push(rect.left + rect.width / 2);
              ys.push(rect.top + rect.height / 2);
            }
          }
        }
        if (xs.length === 0) {
          throw new Error(
            `DeployTargetAnimation encrazed_bombardier_fire_arc: no slot elements in DOM for ${opponentZoneKey}`
          );
        }
        const aimX = xs.reduce((a, b) => a + b, 0) / xs.length;
        const aimY = ys.reduce((a, b) => a + b, 0) / ys.length;
        const minX = Math.min(...xs);
        const maxX = Math.max(...xs);
        const rowHalfWidth = Math.max((maxX - minX) / 2, 48);
        return { aimX, aimY, rowHalfWidth };
      };

      const tryResolveAndPlay = (attempt) => {
        if (fireArcEffectAborted) {
          return;
        }
        try {
          const fromPixel = getSlotPixelPosition(
            fromSlot.zone,
            fromSlot.index,
            fromSlot.playerId,
            fromSlot.instanceId
          );

          const badFrom = fromPixel.x === 0 && fromPixel.y === 0;
          if (badFrom && attempt < 5) {
            timeoutRef.current = setTimeout(() => tryResolveAndPlay(attempt + 1), 60 + attempt * 40);
            return;
          }
          if (badFrom) {
            console.error('❌ DeployTargetAnimation: Encrazed Bombardier fire arc could not resolve source slot (DOM/cell)', { fromSlot });
            isAnimatingRef.current = false;
            fireArcPlaybackStartedRef.current = false;
            onCompleteRef.current?.();
            return;
          }

          const targetRowZoneKey = getTargetRowZoneKey(fromSlot.zone);
          let aimY;
          let rowHalfWidth;
          try {
            ({ aimY, rowHalfWidth } = getOpponentRowAimFromDom(targetRowZoneKey));
          } catch (zoneErr) {
            if (attempt < 5) {
              timeoutRef.current = setTimeout(() => tryResolveAndPlay(attempt + 1), 60 + attempt * 40);
              return;
            }
            throw zoneErr;
          }

          // Lanes are stacked vertically: the enemy row is only above or below, never left/right of the deployer.
          // Row centroid X can sit left/right of the deploy slot; atan2(dx,dy) would wrongly tilt the wave sideways.
          const verticalSpan = Math.abs(aimY - fromPixel.y);
          if (verticalSpan < 1) {
            throw new Error('DeployTargetAnimation encrazed_bombardier_fire_arc: zero vertical span to mirror row');
          }
          // Viewer-relative (BattleUI sets fromSlot.playerId): my deploy → toward opponent (up); opponent deploy → toward me (down).
          if (fromSlot.playerId !== 'your' && fromSlot.playerId !== 'opponent') {
            throw new Error(
              `DeployTargetAnimation encrazed_bombardier_fire_arc: fromSlot.playerId must be 'your' or 'opponent', got ${JSON.stringify(fromSlot.playerId)}`
            );
          }
          // Empirical: CSS/SVG chain makes 0°/180° opposite of naive “0 = up”. Player (your) must open toward top; opponent toward bottom.
          const rotationDeg = fromSlot.playerId === 'your' ? 180 : 0;
          const maxR = typeof window !== 'undefined' ? Math.min(window.innerWidth * 0.38, 480) : 480;
          const baseR = Math.min(
            maxR,
            Math.max(rowHalfWidth * 1.45, verticalSpan * 0.62, 130)
          );

          startTimeRef.current = Date.now();

          const tick = () => {
            if (fireArcEffectAborted) {
              return;
            }
            const elapsed = Date.now() - startTimeRef.current;
            const DURATION = Math.round(BASE_DURATION_MS / speedFactor);
            const t = Math.min(elapsed / DURATION, 1);
            const eased = 1 - Math.pow(1 - t, 2);
            const scale = 0.25 + 0.95 * eased;
            const opacityWave = t < 0.65 ? 1 : 1 - (t - 0.65) / 0.35;
            setFireArc({
              cx: fromPixel.x,
              cy: fromPixel.y,
              rotationDeg,
              r: baseR,
              scale,
              opacity: Math.max(0, opacityWave),
            });

            if (t < 1) {
              fireArcRafRef.current = requestAnimationFrame(tick);
            } else {
              isAnimatingRef.current = false;
              fireArcRafRef.current = null;
              setFireArc(null);
              onFireArcHealthCountdownCompleteRef.current?.();
              onCompleteRef.current?.();
            }
          };

          fireArcRafRef.current = requestAnimationFrame(tick);
        } catch (err) {
          console.error('❌ DeployTargetAnimation: fire arc failed', err);
          isAnimatingRef.current = false;
          fireArcPlaybackStartedRef.current = false;
          setFireArc(null);
          onCompleteRef.current?.();
        }
      };

      isAnimatingRef.current = true;
      timeoutRef.current = setTimeout(() => {
        if (!fireArcEffectAborted) {
          tryResolveAndPlay(0);
        }
      }, 50);

      return () => {
        fireArcEffectAborted = true;
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        if (fireArcRafRef.current) {
          cancelAnimationFrame(fireArcRafRef.current);
          fireArcRafRef.current = null;
        }
        isAnimatingRef.current = false;
        setFireArc(null);
      };
    }

    if (isAnimatingRef.current) {
      console.log('⚠️ DeployTargetAnimation: Skipping - already animating', { fromSlot, toSlot, isAnimating: isAnimatingRef.current });
      return;
    }

    console.log(`🎬 DeployTargetAnimation: Effect triggered`, { fromSlot, toSlot, animationType });
    
    // For general or drone targets, add a small delay to ensure DOM is ready
    const isGeneralTarget = toSlot.zone === 'general';
    const isFromDroneSlot =
      fromSlot.zone === 'your_drone' || fromSlot.zone === 'opponent_drone';
    const isDroneTargetDelayed =
      toSlot.zone === 'your_drone' ||
      toSlot.zone === 'opponent_drone' ||
      isFromDroneSlot;
    
    const startAnimation = () => {
      // Get slot keys for coordinate lookup (skip for general targets)
      const fromKey = fromSlot.zone === 'general' ? null : `${fromSlot.zone}_${fromSlot.index}`;
      const toKey = toSlot.zone === 'general' ? null : `${toSlot.zone}_${toSlot.index}`;
      
      // For non-general, non-drone targets, check coordinates (drone zones use slotCoordinates in getSlotPixelPosition)
      const isDroneTarget = toSlot.zone === 'your_drone' || toSlot.zone === 'opponent_drone';
      if (fromSlot.zone !== 'general' && toSlot.zone !== 'general' && !isDroneTarget) {
        const fromCoords = slotCoordinates[fromKey];
        const toCoords = slotCoordinates[toKey];
        
        if (!fromCoords || !toCoords) {
          console.warn(`⚠️ DeployTargetAnimation: Missing coordinates for ${fromKey} or ${toKey}`);
          console.warn(`⚠️ Available keys:`, Object.keys(slotCoordinates));
          if (onComplete) onComplete();
          return;
        }
      }
      // Convert cell coordinates to pixel coordinates (or DOM lookup for generals)
      const fromPixel = fromSlot.cachedSourcePixel
        ? fromSlot.cachedSourcePixel
        : getSlotPixelPosition(
            fromSlot.zone,
            fromSlot.index,
            fromSlot.playerId,
            fromSlot.instanceId
          );
      // Use cached drone position when available (captured at message receive before state update removed drone)
      const toPixel =
        isDroneTarget && toSlot.cachedTargetPixel
          ? toSlot.cachedTargetPixel
          : getSlotPixelPosition(
              toSlot.zone,
              toSlot.index,
              toSlot.playerId,
              toSlot.instanceId
            );
      
      // Validate positions (generals and drones use getSlotPixelPosition; avoid (0,0) for them)
      if (toPixel.x === 0 && toPixel.y === 0 && (toSlot.zone === 'general' || isDroneTarget)) {
        console.error(`❌ DeployTargetAnimation: Failed to get position for general target (playerId: ${toSlot.playerId})`);
        if (onComplete) onComplete();
        return;
      }
          
      // Log animation start
      if (fromSlot.zone === 'general' || toSlot.zone === 'general' || isDroneTarget) {
        console.log(`🎬 DeployTargetAnimation: Starting animation from ${fromSlot.zone}${fromSlot.index !== null ? `[${fromSlot.index}]` : ''} to ${toSlot.zone}${toSlot.index !== null ? `[${toSlot.index}]` : ''} (playerId: ${toSlot.playerId})`);
      } else {
        const fromCoords = slotCoordinates[fromKey];
        const toCoords = slotCoordinates[toKey];
        if (fromCoords && toCoords) {
          console.log(`🎬 DeployTargetAnimation: Starting animation from ${fromKey} (cell ${fromCoords.cellX},${fromCoords.cellY}) to ${toKey} (cell ${toCoords.cellX},${toCoords.cellY})`);
        } else {
          console.log(`🎬 DeployTargetAnimation: Starting animation from ${fromKey} to ${toKey}`);
        }
      }
      console.log(`🎬 Pixel coordinates: from (${fromPixel.x}, ${fromPixel.y}) to (${toPixel.x}, ${toPixel.y})`);
      
      isAnimatingRef.current = true;
      startTimeRef.current = Date.now();
      setPosition(fromPixel);
      setOpacity(1);
      playSourceAttackSound();
      // Guided Missile convention: image top points at target (atan2 + 90°)
      if (isRotatingDeployProjectile) {
        const dx0 = toPixel.x - fromPixel.x;
        const dy0 = toPixel.y - fromPixel.y;
        const angleRad0 = Math.atan2(dy0, dx0);
        setDaggerRotationDeg((angleRad0 * 180) / Math.PI + 90);
      }

      // Duration scaled by speed multiplier (lower multiplier = longer duration)
      const speedFactor = animationSpeedMultiplier > 0 ? animationSpeedMultiplier : 1;
      const baseDurationMs = isSkyfireStrikeBlueStick ? 320 : 250;
      const DURATION = Math.round(baseDurationMs / speedFactor);
      
      const animate = () => {
        const elapsed = Date.now() - startTimeRef.current;
        const progress = Math.min(elapsed / DURATION, 1);
        
        // Ease-in-out easing
        const eased = progress < 0.5
          ? 2 * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 2) / 2;
        
        // Interpolate position
        const currentX = fromPixel.x + (toPixel.x - fromPixel.x) * eased;
        const currentY = fromPixel.y + (toPixel.y - fromPixel.y) * eased;
        
        setPosition({ x: currentX, y: currentY });
        if (deployAnimationVariant === 'toxengineer_black_smoke' || isToxengineerDeploy) {
          setSmokeTrail({
            fromX: fromPixel.x,
            fromY: fromPixel.y,
            toX: toPixel.x,
            toY: toPixel.y,
            progress: eased
          });
        }
        if (isRotatingDeployProjectile) {
          const dx = toPixel.x - currentX;
          const dy = toPixel.y - currentY;
          const angleRad = Math.atan2(dy, dx);
          setDaggerRotationDeg((angleRad * 180) / Math.PI + 90);
        }

        if (progress < 1) {
          const rafId = requestAnimationFrame(animate);
          animationRef.current = rafId;
        } else {
          // Animation complete
          console.log(`✅ DeployTargetAnimation: Animation complete`);
          isAnimatingRef.current = false;
          animationRef.current = null;
          setSmokeTrail(null);
          if (onDamageAtTarget) {
            onDamageAtTarget({
              x: toPixel.x,
              y: toPixel.y,
              zone: toSlot.zone,
              index: toSlot.index,
              playerId: toSlot.playerId,
              targetType: isGeneralTarget ? 'general' : undefined,
            });
          }
          if (onComplete) onComplete();
        }
      };
      
      const rafId = requestAnimationFrame(animate);
      animationRef.current = rafId;
    };
    
    if (isGeneralTarget || isDroneTargetDelayed) {
      // Small delay so DOM is ready (general position, drone elements with data-drone-zone)
      timeoutRef.current = setTimeout(() => {
        startAnimation();
      }, 50);
      
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
          animationRef.current = null;
        }
        isAnimatingRef.current = false;
      };
    } else {
      startAnimation();
      
      return () => {
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
          animationRef.current = null;
        }
        isAnimatingRef.current = false;
        setSmokeTrail(null);
      };
    }
  }, [fromSlot?.zone, fromSlot?.index, fromSlot?.playerId, toSlot?.zone, toSlot?.index, toSlot?.playerId, deployUnitId, deployAnimationVariant, animationSpeedMultiplier]);

  // Determine projectile style based on animationType (ignored when using Kek / dagger image)
  const getProjectileStyle = () => {
    const baseStyle = {
      position: 'fixed',
      left: `${position.x}px`,
      top: `${position.y}px`,
      transform: 'translate(-50%, -50%)',
      width: useKekAtomImage ? '40px' : '30px',
      height: useKekAtomImage ? '40px' : '30px',
      borderRadius: '50%',
      pointerEvents: 'none',
      zIndex: DEPLOY_VFX_Z_INDEX,
      opacity: opacity,
      transition: 'none'
    };

    if (useKekAtomImage) {
      return { ...baseStyle, backgroundColor: 'transparent', border: 'none', boxShadow: 'none' };
    }

    switch (animationType) {
      case 'friendly':
        return {
          ...baseStyle,
          backgroundColor: 'white',
          boxShadow: '0 0 10px rgba(255, 255, 255, 0.8)',
          border: '2px solid rgba(255, 255, 255, 0.5)'
        };
      case 'opponent':
        return {
          ...baseStyle,
          backgroundColor: 'black',
          boxShadow: '0 0 10px rgba(0, 0, 0, 0.8)',
          border: '2px solid rgba(0, 0, 0, 0.5)'
        };
      case 'both':
        return {
          ...baseStyle,
          backgroundColor: 'yellow',
          boxShadow: '0 0 15px rgba(255, 255, 0, 0.9)',
          border: '2px solid rgba(255, 255, 0, 0.7)',
          width: '40px',
          height: '40px'
        };
      default:
        return baseStyle;
    }
  };

  if (deployAnimationVariant === 'encrazed_bombardier_fire_arc') {
    if (!fireArc) {
      return null;
    }
    const { cx, cy, rotationDeg, r, scale, opacity: arcOpacity } = fireArc;
    const w = r * 2;
    const h = r + 24;
    return (
      <svg
        width={w + 32}
        height={h + 16}
        style={{
          position: 'fixed',
          left: `${cx}px`,
          top: `${cy}px`,
          overflow: 'visible',
          pointerEvents: 'none',
          zIndex: DEPLOY_VFX_Z_INDEX,
          // Pivot at deploy slot (bottom of wave), not the SVG center — wrong origin made the arc swing sideways.
          transformOrigin: '50% 100%',
          transform: `translate(-50%, -100%) rotate(${rotationDeg}deg) scale(${scale})`,
          opacity: arcOpacity,
        }}
        aria-hidden
      >
        <defs>
          <linearGradient id={fireArcGradientId} x1="0%" y1="100%" x2="0%" y2="0%">
            <stop offset="0%" stopColor="#ff5a00" stopOpacity="0.95" />
            <stop offset="45%" stopColor="#ffcc33" stopOpacity="0.75" />
            <stop offset="100%" stopColor="#fff5a0" stopOpacity="0.08" />
          </linearGradient>
          <filter id={`${fireArcGradientId}-blur`} x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="4" />
          </filter>
        </defs>
        <g filter={`url(#${fireArcGradientId}-blur)`}>
          <path
            d={`M ${16 + r} ${h} L ${16} ${h} A ${r} ${r} 0 0 0 ${16 + w} ${h} Z`}
            fill={`url(#${fireArcGradientId})`}
          />
        </g>
        <path
          d={`M ${16 + r} ${h} L ${16} ${h} A ${r} ${r} 0 0 0 ${16 + w} ${h} Z`}
          fill={`url(#${fireArcGradientId})`}
          opacity={0.55}
        />
      </svg>
    );
  }

  // ✅ FIX: Always render if we have a position (even if animation hasn't started yet)
  // The position will be set immediately when animation starts
  if (position.x === 0 && position.y === 0 && !isAnimatingRef.current) {
    return null;
  }

  // Skyfire Strike (275): glowing light-blue stick from drone to enemy general profile
  if (isSkyfireStrikeBlueStick) {
    const stickW = 14;
    const stickH = 56;
    return (
      <svg
        width={stickW}
        height={stickH}
        viewBox={`0 0 ${stickW} ${stickH}`}
        style={{
          position: 'fixed',
          left: `${position.x}px`,
          top: `${position.y}px`,
          transform: `translate(-50%, -50%) rotate(${daggerRotationDeg}deg)`,
          pointerEvents: 'none',
          zIndex: DEPLOY_VFX_Z_INDEX,
          opacity,
          transition: 'none',
          overflow: 'visible',
        }}
        aria-hidden
      >
        <defs>
          <filter
            id={`${crossGlowFilterId}-skyfire`}
            x="-100%"
            y="-100%"
            width="300%"
            height="300%"
          >
            <feGaussianBlur in="SourceGraphic" stdDeviation="3.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <g filter={`url(#${crossGlowFilterId}-skyfire)`}>
          <rect x="2" y="2" width="10" height="52" rx="5" fill="#b8f4ff" />
          <rect x="3.5" y="5" width="7" height="46" rx="3.5" fill="#5ce8ff" opacity="0.92" />
          <rect x="5" y="8" width="4" height="40" rx="2" fill="#e8fcff" opacity="0.75" />
        </g>
      </svg>
    );
  }

  // KINSHIP boss (EU mission 74): black damage stick from bot general to player general
  if (isKinshipStrikeBlackStick) {
    const stickW = 14;
    const stickH = 56;
    return (
      <svg
        width={stickW}
        height={stickH}
        viewBox={`0 0 ${stickW} ${stickH}`}
        style={{
          position: 'fixed',
          left: `${position.x}px`,
          top: `${position.y}px`,
          transform: `translate(-50%, -50%) rotate(${daggerRotationDeg}deg)`,
          pointerEvents: 'none',
          zIndex: DEPLOY_VFX_Z_INDEX,
          opacity,
          transition: 'none',
          overflow: 'visible',
        }}
        aria-hidden
      >
        <defs>
          <filter
            id={`${crossGlowFilterId}-kinship`}
            x="-100%"
            y="-100%"
            width="300%"
            height="300%"
          >
            <feGaussianBlur in="SourceGraphic" stdDeviation="3.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <g filter={`url(#${crossGlowFilterId}-kinship)`}>
          <rect x="2" y="2" width="10" height="52" rx="5" fill="#1a1a1a" />
          <rect x="3.5" y="5" width="7" height="46" rx="3.5" fill="#3d3d3d" opacity="0.92" />
          <rect x="5" y="8" width="4" height="40" rx="2" fill="#8a8a8a" opacity="0.55" />
        </g>
      </svg>
    );
  }

  // Frontal Harassment Unit (100): glowing light-blue cross from unit slot to enemy general
  if (isFrontalHarassmentBlueCross) {
    const crossSize = 44;
    return (
      <svg
        width={crossSize}
        height={crossSize}
        viewBox="0 0 48 48"
        style={{
          position: 'fixed',
          left: `${position.x}px`,
          top: `${position.y}px`,
          transform: `translate(-50%, -50%) rotate(${daggerRotationDeg}deg)`,
          pointerEvents: 'none',
          zIndex: DEPLOY_VFX_Z_INDEX,
          opacity,
          transition: 'none',
          overflow: 'visible',
        }}
        aria-hidden
      >
        <defs>
          <filter id={`${crossGlowFilterId}-glow`} x="-80%" y="-80%" width="260%" height="260%">
            <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <g filter={`url(#${crossGlowFilterId}-glow)`}>
          <rect x="20" y="6" width="8" height="36" rx="2" fill="#b8f4ff" />
          <rect x="6" y="20" width="36" height="8" rx="2" fill="#b8f4ff" />
          <rect x="20" y="6" width="8" height="36" rx="2" fill="#5ce8ff" opacity="0.85" />
          <rect x="6" y="20" width="36" height="8" rx="2" fill="#5ce8ff" opacity="0.85" />
        </g>
      </svg>
    );
  }

  // Redeem damage (Youthful Patriot, Rooter for the Cause, etc.): redeem_bullets.png from source to target
  if (isRedeemBulletAnimation) {
    return (
      <img
        src="/images/redeem_bullets.png"
        alt=""
        style={{
          position: 'fixed',
          left: `${position.x}px`,
          top: `${position.y}px`,
          transform: `translate(-50%, -50%) rotate(${daggerRotationDeg}deg)`,
          width: '53px',
          height: 'auto',
          maxHeight: '132px',
          pointerEvents: 'none',
          zIndex: DEPLOY_VFX_Z_INDEX,
          opacity,
          transition: 'none'
        }}
      />
    );
  }

  // First Wave Ascendants (71), Second Wave Redeemers (73): bullet.png; orientation matches Guided Missile (317)
  if (isBulletDeployUnit) {
    return (
      <img
        src="/images/bullet.png"
        alt=""
        style={{
          position: 'fixed',
          left: `${position.x}px`,
          top: `${position.y}px`,
          transform: `translate(-50%, -50%) rotate(${daggerRotationDeg}deg)`,
          width: '53px',
          height: 'auto',
          maxHeight: '132px',
          pointerEvents: 'none',
          zIndex: DEPLOY_VFX_Z_INDEX,
          opacity,
          transition: 'none'
        }}
      />
    );
  }

  // Nomadic Bladecaster (70): dagger.png; same easing/duration as default deploy; orientation matches Guided Missile (317)
  if (isDaggerDeployUnit) {
    return (
      <img
        src="/images/dagger.png"
        alt=""
        style={{
          position: 'fixed',
          left: `${position.x}px`,
          top: `${position.y}px`,
          transform: `translate(-50%, -50%) rotate(${daggerRotationDeg}deg)`,
          width: '53px',
          height: 'auto',
          maxHeight: '132px',
          pointerEvents: 'none',
          zIndex: DEPLOY_VFX_Z_INDEX,
          opacity,
          transition: 'none'
        }}
      />
    );
  }

  if (useKekAtomImage) {
    return (
      <div style={getProjectileStyle()}>
        <img
          src="/images/kek_atom.png"
          alt=""
          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
      </div>
    );
  }

  if ((deployAnimationVariant === 'toxengineer_black_smoke' || isToxengineerDeploy) && smokeTrail) {
    const dx = smokeTrail.toX - smokeTrail.fromX;
    const dy = smokeTrail.toY - smokeTrail.fromY;
    const trailLength = Math.hypot(dx, dy) || 1;
    const ux = dx / trailLength;
    const uy = dy / trailLength;
    const puffs = [0, 1, 2, 3, 4].map((idx) => {
      const trailingFactor = Math.max(0, smokeTrail.progress - idx * 0.12);
      const x = smokeTrail.fromX + dx * trailingFactor - ux * idx * 10;
      const y = smokeTrail.fromY + dy * trailingFactor - uy * idx * 10;
      const size = 18 + idx * 6;
      const alpha = Math.max(0.14, 0.65 - idx * 0.1);
      return { idx, x, y, size, alpha };
    });
    return (
      <>
        {puffs.map((puff) => (
          <div
            key={`tox-smoke-${puff.idx}`}
            style={{
              position: 'fixed',
              left: `${puff.x}px`,
              top: `${puff.y}px`,
              width: `${puff.size}px`,
              height: `${puff.size}px`,
              borderRadius: '50%',
              transform: 'translate(-50%, -50%)',
              pointerEvents: 'none',
              zIndex: DEPLOY_VFX_Z_INDEX,
              background: `radial-gradient(circle, rgba(15,15,15,${puff.alpha}) 0%, rgba(0,0,0,${Math.max(0.05, puff.alpha - 0.25)}) 65%, rgba(0,0,0,0) 100%)`,
              filter: 'blur(1px)'
            }}
          />
        ))}
      </>
    );
  }

  return (
    <div style={getProjectileStyle()} />
  );
};

export default DeployTargetAnimation;

